import React from 'react'

import styled from 'styled-components'
import { styleTheme } from '../../../../style/theme'
import { getPrimaryColor } from '../../../../style/utils'
import { useWindowSize } from 'customer/hooks/useWindowSize'
import type { PickupParticularFair } from '../../BridalFairs.types'

import { format, isBefore, isSameDay, setHours, setMinutes, subDays } from 'date-fns'
import ja from 'date-fns/locale/ja'
import { FiClock } from 'react-icons/fi'
import { Availability } from '../../../../components/ui-elements/Icons/Availability'
import { ImageComponent } from '../../../../components/ui-elements/Image'

type Props = {
  pickupParticularFair: PickupParticularFair
  column?: boolean
  primaryColor?: string
  bridalFairPageKey: string
  holidays: { name: string; date: Date }[]
}

export const RecommendedBridalFairs: React.FC<Props> = (props) => <StyledComponent {...props} />

const MOBILE_BREAKPOINT = 520

const Component: React.FC<Props & { className?: string }> = ({
  column,
  className,
  pickupParticularFair,
  bridalFairPageKey,
  holidays,
}) => {
  const { width } = useWindowSize()

  const initialFairTime = pickupParticularFair.fairTimes[0]
  const fairTimes = initialFairTime
    ? pickupParticularFair.fairTimes.filter((fairTime) => isSameDay(initialFairTime.startAt, fairTime.startAt))
    : []
  const displayFairTimes = fairTimes.splice(0, 4)
  const isNetReservable = (): boolean => {
    const deadlineDate = subDays(initialFairTime.startAt, pickupParticularFair.bridalFairDetail.netResvDeadlineDays)
    const [hours, minutes] = pickupParticularFair.bridalFairDetail.netResvDeadlineTime.split(':').map(Number)
    const deadlineDateTime = setMinutes(setHours(deadlineDate, hours), minutes)
    return isBefore(new Date(), deadlineDateTime)
  }

  const aspectRatio = width <= MOBILE_BREAKPOINT || column ? 'threeToTwoImage' : 'oneToOneImage'

  return (
    <a
      className={className}
      key={pickupParticularFair.bridalFairDetail.key}
      data-column={!!column}
      href={`/customer/public/bridal_fair_pages/${bridalFairPageKey}/fairs/${
        pickupParticularFair.bridalFairDetail.key
      }?fair_date=${format(initialFairTime.startAt, 'yyyy-MM-dd')}`}
      rel="noopener noreferrer"
      target="_blank"
    >
      <span className="text-label">イチオシ</span>
      {pickupParticularFair.imageData && (
        <div className="recommended-fair-image" data-column={!!column}>
          <ImageComponent
            imageData={pickupParticularFair.imageData}
            aspectRatio={aspectRatio}
            altText={pickupParticularFair.name}
            className="fair-image"
          />
        </div>
      )}
      <div
        className="recommended-fair-detail"
        data-day={
          initialFairTime
            ? initialFairTime.startAt.getDay() === 0 ||
              holidays.some((holiday) => isSameDay(holiday.date, new Date(initialFairTime.startAt)))
              ? 'holiday'
              : initialFairTime.startAt.getDay() === 6
                ? 'saturday'
                : ''
            : ''
        }
        data-column={!!column}
      >
        <div className="name">{pickupParticularFair.name}</div>
        <div className="date-wrapper">
          {initialFairTime && (
            <>
              <span className="date">{format(initialFairTime.startAt, 'M/d')}</span>
              <span className="day">{format(initialFairTime.startAt, '(eee)', { locale: ja })}</span>
              <span className="time">
                <FiClock />
                {pickupParticularFair.bridalFair.minutes}分
              </span>
            </>
          )}
        </div>
        <div className="availability">
          {displayFairTimes.map((fairTime, index) => (
            <div key={`${format(fairTime.startAt, 'HH:mm')}.${index}`}>
              {index !== 0 && index !== displayFairTimes.length && <span>|</span>}
              <span>
                <Availability availability={isNetReservable() ? fairTime.availability : 'none'} />
              </span>
              <span>{`${format(fairTime.startAt, 'HH:mm')}~`}</span>
            </div>
          ))}
          {fairTimes.length > 0 && <div className="extra-available-time">...</div>}
        </div>
        <div className="show-link">
          <a
            href={`/customer/public/bridal_fair_pages/${bridalFairPageKey}/fairs/${
              pickupParticularFair.bridalFairDetail.key
            }?fair_date=${format(initialFairTime.startAt, 'yyyy-MM-dd')}`}
            rel="noopener noreferrer"
            target="_blank"
          >
            詳しく見る
          </a>
        </div>
      </div>
    </a>
  )
}

const StyledComponent = styled(Component)`
  width: 100%;
  padding: 4px;
  display: flex;
  position: relative;
  border: 1px solid ${styleTheme.colors.gray.baseLight};
  box-sizing: border-box;
  background-color: ${styleTheme.colors.white};
  text-decoration: none;

  &[data-column='true'] {
    width: 100%;
    height: 100%;
    max-width: 240px;
    max-height: none;
    margin-right: 0;
    flex-direction: column;
    @media (max-width: 480px) {
      max-width: 340px;
    }
  }

  .text-label {
    font-family: Noto Sans JP;
    font-size: 12px;
    font-style: normal;
    font-weight: 700;
    line-height: 100%;
    position: absolute;
    top: 8px;
    left: 0;
    z-index: 2;
    color: ${styleTheme.colors.white};
    background-color: ${getPrimaryColor};
    padding: 2px 4px;
  }

  > .recommended-fair-image {
    max-height: 192px;
    margin-right: 16px;

    > .fair-image {
      width: 192px;
      height: 192px;
      object-fit: cover;
      object-position: center;
    }

    &[data-column='true'] {
      width: 100%;
      padding-top: 66.66%;
      margin-right: 0;
      position: relative;

      > .fair-image {
        width: 100%;
        height: auto;
        position: absolute;
        top: 0;
        left: 0;
      }
    }
  }

  > .recommended-fair-detail {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
    height: 100%;

    &[data-day='holiday'] .day {
      color: ${styleTheme.colors.red.base};
    }

    &[data-day='saturday'] .day {
      color: ${styleTheme.colors.blue.base};
    }

    &[data-column='true'] {
      max-width: 100%;
      padding: 16px;
      box-sizing: border-box;
      height: 225px;
    }

    > .name {
      color: ${styleTheme.colors.black};
      font-family: Noto Sans JP;
      font-size: 14px;
      font-style: normal;
      font-weight: 700;
      line-height: 1.5;
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      padding-top: 8px;
      height: 72px;
    }

    > .date-wrapper {
      display: flex;
      align-items: end;
      gap: 4px;

      > span {
        color: ${styleTheme.colors.black};
        font-family: Inter;
        font-size: 18px;
        font-style: normal;
        font-weight: 600;
        line-height: 1.5;
      }

      > .date {
        font-size: 24px;
        line-height: 1.25;
      }

      > .time {
        font-weight: 400;
        display: flex;
        align-items: center;
        gap: 4px;
        margin-left: 4px;
        font-size: 14px;
        white-space: nowrap;
      }
    }

    > .availability {
      display: flex;
      justify-content: start;
      gap: 4px;
      flex-wrap: wrap;
      font-size: 14px;
      height: 32px;

      > div {
        display: flex;
        gap: 3px;
        align-items: center;
      }

      > .extra-available-time {
        font-size: 12px;
        text-align: center;
      }
    }

    > .show-link {
      display: flex;
      justify-content: flex-end;
      margin-top: auto;

      a {
        color: ${getPrimaryColor};
        font-family: Inter;
        font-size: 12px;
        font-style: normal;
        font-weight: 400;
        line-height: 150%;
        text-decoration-line: underline;
        padding: 8px;
      }
    }
  }

  @media (max-width: 520px) {
    width: 100%;
    height: 100%;
    max-width: none;
    max-height: none;
    margin-right: 0;
    flex-direction: column;

    > .recommended-fair-image {
      width: 100%;
      padding-top: 66.66%;
      margin-right: 0;
      position: relative;

      > .fair-image {
        width: 100%;
        height: auto;
        position: absolute;
        top: 0;
        left: 0;
        object-fit: cover;
      }
    }

    > .recommended-fair-detail {
      max-width: 100%;
      padding: 4px 16px 16px 16px;
      box-sizing: border-box;
      height: 225px;
    }
  }
`
